import { type Theme } from '@mui/material'
import {
  type DataGridProps,
  gridClasses,
  type GridColDef,
  type MuiEvent
} from '@mui/x-data-grid'
import React from 'react'

import {
  getLightMuiBackgroundColor,
  getLightMuiBackgroundColorHover
} from 'utils/colors'
import { isTestEnvironment } from 'utils/environment'

import type { ScenarioRunStatus } from 'types/api/scenarios/runs/scenario-runs'

/** Reusable utilities, constants, etc for MUI DataGrid */

const DEFAULT_PAGE_SIZE = 10

export const defaultGridProps: Partial<DataGridProps> = {
  autoHeight: true,
  disableSelectionOnClick: true,
  disableVirtualization: isTestEnvironment(), // see https://github.com/mui/mui-x/issues/1519
  pageSize: DEFAULT_PAGE_SIZE,
  rowsPerPageOptions: [DEFAULT_PAGE_SIZE]
}

export const defaultColumnProps: Partial<GridColDef> = {
  disableColumnMenu: true,
  disableReorder: true,
  filterable: false,
  sortable: false
}

const sxGridDefault = {
  /**
   * Disable blue border outlining the cell with focus:
   * https://github.com/mui/mui-x/issues/8104#issuecomment-1453945311
   */
  [`& .${gridClasses.cell}:focus-within`]: {
    outline: 0
  },

  [`& .${gridClasses.root}`]: {
    whiteSpace: 'break-spaces'
  },
  [`& .${gridClasses.columnSeparator}`]: {
    display: 'none'
  }
}

export const sxClickableRow = {
  [`& .${gridClasses.row}`]: { cursor: 'pointer' }
}

export function applyPassFailClassNames(status: ScenarioRunStatus) {
  if (status === 'Failed') {
    return 'test-failed'
  }

  if (status === 'Passed') {
    return 'test-passed'
  }

  return ''
}

export function applyPassFailRowColors({
  clickableRow
}: {
  clickableRow?: boolean
} = {}) {
  return {
    [`& .${gridClasses.row}.test-failed`]: (muiTheme: Theme) => ({
      backgroundColor: getLightMuiBackgroundColor(
        muiTheme.palette.warning.light
      ),
      ...(clickableRow && {
        '&:hover': {
          backgroundColor: getLightMuiBackgroundColorHover(
            muiTheme.palette.warning.light
          )
        }
      })
    }),
    [`& .${gridClasses.row}.test-passed`]: (muiTheme: Theme) => ({
      backgroundColor: getLightMuiBackgroundColor(
        muiTheme.palette.success.light
      ),
      ...(clickableRow && {
        '&:hover': {
          backgroundColor: getLightMuiBackgroundColorHover(
            muiTheme.palette.success.light
          )
        }
      })
    })
  }
}

export function getGridStyles(
  options: {
    clickableRow?: boolean
    cellPaddingY?: number
    hideBorder?: boolean
    hidePaginationDisplayedRows?: boolean
    minHeight?: number | string
  } = {}
) {
  const {
    clickableRow = false,
    cellPaddingY = 1,
    hideBorder = true,
    hidePaginationDisplayedRows = false,
    minHeight
  } = options

  return {
    ...sxGridDefault,
    ...(hideBorder && {
      border: 0
    }),
    [`& .${gridClasses.cell}`]: {
      py: cellPaddingY
    },
    ...(clickableRow && sxClickableRow),
    ...(hidePaginationDisplayedRows && {
      ['& .MuiTablePagination-displayedRows']: {
        display: 'none'
      }
    }),
    ...(minHeight !== undefined && {
      [`& .${gridClasses.main}`]: {
        minHeight
      }
    })
  }
}

/**
 * General DOM utility
 */
export function getIsTextSelected() {
  return Boolean(window.getSelection()?.toString())
}

/**
 * The callback action is allowed if
 * - the click target is the row itself. This enables other row actions without adding event.stopPropagation() to their onClick handlers
 * - no text is selected on the screen. This allows the user to copy text without navigating away before they're done
 */
export function safeRowClick(
  event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
  callback: () => unknown
) {
  // @ts-expect-error this is intentionally permissive
  const isDataGridCell = Array.from(event?.target?.classList ?? []).includes(
    gridClasses.cell
  )

  if (isDataGridCell && !getIsTextSelected()) {
    callback()
  }
}
